// İSG Portal — Tooltip primitifi (`@radix-ui/react-tooltip` sarmalayıcısı). Tetikleyici
// HER ZAMAN gerçek, odaklanabilir bir `<button>`; Radix'in yerleşik davranışı tooltip'i
// hem hover'da hem `focus`'ta açar (bkz. kütüphanenin `onFocus` → `context.onOpen()`,
// gecikme yok) — bu yüzden klavye erişimi için ekstra kod GEREKMEZ. Katman token'ı
// `--z-dropdown` ([[feedback_ui_layer_order]]); literal renk/z-index yok.
//
// KURAL (bu component'i kullanan HER ekran için geçerli): bir tooltip bir hata/uyarı
// KOŞULUNUN TEK taşıyıcısı OLAMAZ. `label` bunun için ZORUNLU tutulur ve her zaman
// tetikleyicinin `aria-label`'ı olur — ekran okuyucu kullanıcısı tooltip'i AÇMADAN, salt
// tetikleyiciden bir koşulun var olduğunu öğrenir. Bir hata/uyarı KOŞULUNU işaretleyen bir
// tetikleyici için `label` KOŞULUN kendisini anlatır (ör. "Ad belgeyle uyuşmuyor"); `content`
// yalnız NEDENİ taşır. Görünür bir uyarı ikonu/metni de gerekiyorsa (AC8 emsali) bu
// component'in DIŞINDA, satır içinde render edilir — tooltip yalnız EK açıklamadır, birincil
// sinyal değil.
//
// Tıklama (fare/dokunmatik/kalem): Radix'in varsayılan tetikleyicisi HER tıklamada kapatır
// (hover modeli varsayımıyla — kütüphane kaynağında `onClick: composeEventHandlers(...,
// context.onClose)`, doğrulandı). Fare için sonucu: ⓘ'yi AÇMAK için tıklayan kullanıcı onu aynı
// anda KAPATIR; içeriği görmenin tek yolu hover'da gecikmeyi beklemek kalırdı. Dokunmatik/
// kalemde daha da kötüsü olur: bir `pointerdown` odağı (`focus`) bastırır ve tooltip hiç
// açılmadan `click` onu zaten kapalı bulur. Bu yüzden `open` burada KONTROLLÜ tutulur: gerçek
// bir işaretçi tıklaması (fare/dokunmatik/kalem) Radix'in otomatik kapatmasını
// (`event.preventDefault()`) devre dışı bırakıp içeriği kendi AÇ/KAPA döngüsüne alır. "Gerçek"
// ayrımı `event.detail` ile yapılır: tarayıcı fiziksel bir tıklamada `detail`'i >=1 verir,
// klavyeyle (Enter/Boşluk) üretilen `click`'te ise 0 — böyle bir `click`'in önünde `pointerdown`
// hiç YOKTUR, dolayısıyla klavyenin zaten `focus` ile gecikmesiz açtığı tooltip'i (yukarıda)
// kapatan Radix varsayılanına DOKUNULMAZ; klavye davranışı bu değişiklikten ETKİLENMEZ.
//
// Tasarım notu: her `Tooltip` KENDİ `Provider`'ını taşır (bkz. altta) — bir üst `Provider`
// bağlamı iç içe geçtiğinde her zaman EN İÇTEKİ kazanır, yani dışarıda ayrı bir
// `TooltipProvider` mount etmek sessizce hiçbir şey yapmaz. Kayıp: aynı kartta art arda açılan
// birden çok ⓘ arasında "ilk gecikmeli, sonrakiler anında" gruplaması yok — her tetikleyici
// kendi `HOVER_DELAY_MS` gecikmesini çalıştırır. Bu, sıfır-kurulum (Button/Modal/Select ile
// aynı) lehine bilinçli bir ödün; bir sonraki dilim çok sayıda ⓘ'nin aynı kartta yoğunlaştığını
// gösterirse gruplama, açıkça isimlendirilmiş bir izleyen değişikliktir.
//
// `HOVER_DELAY_MS` Radix'in 700ms varsayılanından 300ms'e indirildi: artık tıklama anında açtığı
// için (yukarıda) hover gecikmesinin tek işi kazara geçen imleci elemek — 300ms yaygın
// "hover-intent" eşiğidir, kasıtsız geçişi eler ama kasıtlı hover'ı geciktirmez hissettirir.
//
// **`asChild` (S34-3, design review M5).** İkinci bir mod: tetikleyici bu bileşenin KENDİ
// `<button><Icon/></button>`'u DEĞİL, çağıranın verdiği GERÇEK bir eleman (ör. rail modundaki
// bir `NavLink`, ya da `TableToolbar`'ın yoğunluk düğmesi) — Radix `Trigger`ı yalnız `asChild`
// ile SARAR. Yukarıdaki kontrollü aç/kapa (tıklamada `preventDefault` + kendi `open` state'imiz)
// bu modda BİLİNÇLİ OLARAK YOKTUR: o mantık yalnız tıklamanın BAŞKA HİÇBİR ANLAMI olmadığı ⓘ
// ikonu için gerekliydi (aksi hâlde fare tıklaması içeriği görmeden kapatırdı) — burada
// tetikleyicinin tıklanmasının KENDİ eylemi var (gezinme, yoğunluk değiştirme); `preventDefault`
// o eylemi KIRARDI. Radix'in varsayılan hover/focus AÇMA'sı zaten klavye erişimini karşılar
// (dosya başlığındaki "Radix'in yerleşik davranışı..." notu HER İKİ modda geçerli), tıklamada
// kapanması da BURADA zararsızdır (link zaten aynı anda navigasyona gidiyor). Erişilebilir ad bu
// modda `children`'ın KENDİSİNDEN gelir (görünür metin ya da `sr-only` bir span) — `label` bu
// yüzden bu modda YOKTUR (tip düzeyinde ayrı bir union kolu, aşağıda).

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { LucideIcon } from 'lucide-react';
import { Info } from 'lucide-react';
import { forwardRef, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cn } from './cn';

interface TooltipSharedProps {
  /**
   * Tooltip içeriği — açıklayıcı cümle. Model/kullanıcı kaynaklı güvenilmeyen metin burada
   * yalnız React child olarak render edilir (zaten güvenli — `dangerouslySetInnerHTML` yok).
   */
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Tooltip içerik kutusuna eklenir. */
  contentClassName?: string;
}

export type TooltipProps =
  | (TooltipSharedProps & {
      /** S34-3 — bkz. dosya başlığı "asChild" notu. */
      asChild: true;
      /** Tek, gerçek bir tetikleyici eleman (kendi erişilebilir adını KENDİSİ taşır). */
      children: ReactElement;
    })
  | (TooltipSharedProps & {
      asChild?: false;
      /**
       * Tetikleyicinin erişilebilir adı (`aria-label`) — bu modda ZORUNLU. Bkz. dosya başlığı:
       * bir hata/uyarı KOŞULUNU işaretleyen bir tetikleyici için KOŞULUN kendisini anlatır;
       * sıradan bir açıklama ⓘ'sü için kısa bir söylemdir (ör. "Bu alan hakkında bilgi").
       */
      label: string;
      /** Tetikleyici ikon — varsayılan `Info` (ⓘ). Bir uyarı KOŞULU için ör. `TriangleAlert`. */
      icon?: LucideIcon;
      /** Tetikleyici `<button>`'a eklenir. */
      className?: string;
    });

const triggerBase =
  'inline-flex shrink-0 items-center justify-center rounded p-0.5 text-muted ' +
  'hover:bg-surface-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring';

const contentBase =
  'z-[var(--z-dropdown)] max-w-[18rem] rounded-md border border-border bg-surface ' +
  'px-2.5 py-1.5 text-xs text-fg shadow-md';

// Bkz. dosya başlığı, kapanış notu — Radix varsayılanı (700ms) yerine.
const HOVER_DELAY_MS = 300;

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(function Tooltip(props, ref) {
  const { content, side = 'top', align = 'center', contentClassName } = props;

  // Hooks KOŞULSUZ, her render'da AYNI sırada çağrılır (Hooks Kuralları) — `asChild` dalı
  // aşağıda bunların HİÇBİRİNİ kullanmaz, ama yine de burada, herhangi bir `return`'den ÖNCE
  // çağrılmaları gerekir.
  const [open, setOpen] = useState(false);
  // Son gözlemlenen işaretçi türü — dokunuş/kalem/fare tıklamasını klavyeden ayırt etmek için
  // (bkz. dosya başlığı). `PointerEvent.pointerType` genel `string`'tir.
  const lastPointerTypeRef = useRef<string>('mouse');
  // `click` ANINDAKİ `open` değil, `pointerdown` ANINDAKİ (Radix'in kendi `pointerdown`
  // işleyicisinin ön-kapamasından ÖNCEKİ) `open` — bkz. dosya başlığı. Bizim `onPointerDown`
  // burada `composeEventHandlers`'ta İLK sırada çalışır (kütüphane kaynağında doğrulandı), yani
  // `open`'ı okuduğumuz an Radix henüz dokunmamıştır.
  const wasOpenRef = useRef(false);

  if (props.asChild) {
    // S34-3 (design review M5) — bkz. dosya başlığı "asChild" notu: kontrollü aç/kapa YOK,
    // Radix'in varsayılan hover/focus/tıklama davranışı aynen kullanılır.
    return (
      <TooltipPrimitive.Provider delayDuration={HOVER_DELAY_MS}>
        <TooltipPrimitive.Root>
          <TooltipPrimitive.Trigger asChild>{props.children}</TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              side={side}
              align={align}
              sideOffset={6}
              className={cn(contentBase, contentClassName)}
            >
              {content}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }

  const { label, icon: Icon = Info, className } = props;

  return (
    <TooltipPrimitive.Provider delayDuration={HOVER_DELAY_MS}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger
          asChild
          onPointerDown={(event) => {
            lastPointerTypeRef.current = event.pointerType;
            wasOpenRef.current = open;
          }}
          onClick={(event) => {
            const pointerType = lastPointerTypeRef.current;
            // Klavyeyle (Enter/Boşluk) üretilen `click`'in önünde gerçek bir `pointerdown`
            // YOKTUR, bu yüzden `lastPointerTypeRef` bir önceki gerçek işaretçi etkileşiminde
            // (ya da hiç olmadıysa varsayılan 'mouse'da) kalır — `event.detail` bu "sahte" fare
            // tıklamasını gerçeğinden ayırır (bkz. dosya başlığı).
            const isRealPointerClick =
              pointerType === 'touch' ||
              pointerType === 'pen' ||
              (pointerType === 'mouse' && event.detail > 0);
            if (isRealPointerClick) {
              // Radix'in tetikleyicisi HER tıklamada kapatır (hover modeli varsayımıyla) —
              // bu olmasa fareyle ⓘ'ye tıklayan kullanıcı içeriği hiç görmeden kapatırdı.
              // `preventDefault` kütüphanenin dahili `context.onClose()`'unu iptal eder
              // (bkz. `composeEventHandlers` — `checkForDefaultPrevented`); biz AÇ/KAPA'yı
              // kendimiz yönetiriz. `wasOpenRef` (yukarıda `pointerdown`da yakalandı) karşısında
              // tersine çeviririz — `prev` değil: Radix'in KENDİ `pointerdown` işleyicisi bu
              // `click`'ten ÖNCE, tetikleyici zaten açıkken `context.onClose()` çağırıp `open`'ı
              // `false`'a düşürür (bkz. dosya başlığı); `prev` kullansaydık her tıklama `false`
              // okur ve HER ZAMAN açardı — ikinci tıklama asla kapatamazdı.
              event.preventDefault();
              setOpen(!wasOpenRef.current);
            }
          }}
        >
          <button ref={ref} type="button" aria-label={label} className={cn(triggerBase, className)}>
            <Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={6}
            className={cn(contentBase, contentClassName)}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
});
